import { useContext } from "react";
import {
  MutationDraftContext,
  useMutationScope,
} from "../../stores/MutationDraft/mutationDraft.provider";
import {
  DraftCalendarEvent,
  DraftEventType,
  MutationScope,
} from "../../stores/MutationDraft/types";
import { CalendarEvent } from "./controller/adapters/types";

/**
 * Reads draft events from CalendarAppointmentMutationDraftStore for a specific
 * scope (patientId + locationId) and returns them as CalendarEvent[] that
 * FullCalendar can render directly.
 *
 * Returns an empty array when the provider is not mounted or scope is undefined
 * (safe to call from any calendar that may or may not be inside a MutationDraftProvider).
 */
export const useCalendarDraftEvents = (scopeOverride?: MutationScope): CalendarEvent[] => {
  const registry = useContext(MutationDraftContext);
  // const mutationScope = useMutationScope();
  // const scope = scopeOverride ?? mutationScope;

  if (!registry) return [];
  // if (!registry || !scope) return [];
  const store = registry.getDefaultStore();
  const drafts = store.draftEvents;
  if (drafts.length === 0) return [];

  return drafts.map(draftToCalendarEvent);
};

const draftToCalendarEvent = (d: DraftCalendarEvent): CalendarEvent => {
  const colors = getEventColors(d.type, d.drivingTimeAction);
  return {
    id: d.id,
    resourceId: d.therapistId,
    start: d.start,
    end: d.end,
    title: d.label,
    editable: false,
    startEditable: false,
    durationEditable: false,
    backgroundColor: colors.bg,
    borderColor: colors.border,
    textColor: "#fff",
    display: "auto",
    extendedProps: {
      eventType: "draft",
      timeSlotType:
        d.type === "placement"
          ? "DraftPlacement"
          : d.type === "shift"
            ? "DraftShift"
            : "DraftTravelTime",
      isContinuous: false,
      appointmentId: d.appointmentId,
      timeSlotId: d.timeSlotId,
      originalStart: d.originalStart,
      originalEnd: d.originalEnd,
      originalTherapistId: d.originalTherapistId,
      originalTherapistName: d.originalTherapistName,
      draftId: d.draftId,
      drivingTimeAction: d.drivingTimeAction,
    },
  };
};

function getEventColors(type: DraftEventType, action?: string): { bg: string; border: string } {
  if (type === "driving-time") {
    if (action === "create") return { bg: "#26a69a", border: "#00897b" };
    if (action === "delete") return { bg: "#ef5350", border: "#c62828" };
    return { bg: "#42a5f5", border: "#1976d2" }; // update
  }
  if (type === "placement") return { bg: "#4caf50", border: "#388e3c" };
  return { bg: "#ff9800", border: "#f57c00" }; // shift
}
